import { useState } from 'react';
import { useMasters } from './MastersProvider';
import { createDepartment, updateDepartment } from './masterRepo';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import type { Department } from '@core/lib/types';

export default function DepartmentsTab() {
  const { departments, reload } = useMasters();
  const toast = useToast();
  const [editing, setEditing] = useState<Department | null>(null);
  const [creating, setCreating] = useState<{
    id: string;
    name: string;
    short_name: string;
    display_order: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    if (!creating) return;
    setBusy(true);
    try {
      await createDepartment(creating);
      await reload();
      setCreating(null);
      toast('事業部を追加しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '追加に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!editing) return;
    setBusy(true);
    try {
      await updateDepartment(editing.id, {
        name: editing.name,
        short_name: editing.short_name,
        display_order: editing.display_order,
        active: editing.active,
      });
      await reload();
      setEditing(null);
      toast('事業部を更新しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '更新に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          事業部マスタ({departments.length}事業部)
          <button
            className="btn-line"
            style={{ marginLeft: 'auto' }}
            onClick={() =>
              setCreating({
                id: '',
                name: '',
                short_name: '',
                display_order: (departments.length + 1) * 10,
              })
            }
          >
            事業部を追加
          </button>
        </h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          追加した事業部は<b>汎用の集計(共通サマリー5行)で即座に全社PLへ参加</b>します。
          固有の計算ロジックが必要になった時点で、事業部セッションが `/modules/&lt;事業部ID&gt;/` を作ります。
          使わなくなった事業部は削除せず「無効」にしてください(過去の取引を残すため)。
        </p>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>名称</th>
              <th>短縮名</th>
              <th className="num">表示順</th>
              <th>本部</th>
              <th>状態</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {departments.map((d) => (
              <tr key={d.id}>
                <td className="hint">{d.id}</td>
                <td>{d.name}</td>
                <td>{d.short_name}</td>
                <td className="num">{d.display_order}</td>
                <td>{d.is_headquarters ? <span className="badge b-plan">本部</span> : ''}</td>
                <td>
                  <span className={`badge ${d.active ? 'b-ok' : 'b-ng'}`}>
                    {d.active ? '有効' : '無効'}
                  </span>
                </td>
                <td>
                  <button className="txn-link" onClick={() => setEditing({ ...d })}>
                    編集
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal onClose={() => setCreating(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>事業部を追加</h3>
          </div>
          <div className="form-grid">
            <div>
              <label>事業部ID(英小文字・数字・ハイフン。後から変更不可)</label>
              <input
                autoFocus
                value={creating.id}
                onChange={(e) => setCreating({ ...creating, id: e.target.value })}
                placeholder="例)shinjuku"
              />
            </div>
            <div>
              <label>事業部名</label>
              <input
                value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                placeholder="例)新宿営業所"
              />
            </div>
            <div>
              <label>短縮名(任意)</label>
              <input
                value={creating.short_name}
                onChange={(e) => setCreating({ ...creating, short_name: e.target.value })}
              />
            </div>
            <div>
              <label>表示順</label>
              <input
                type="number"
                value={creating.display_order}
                onChange={(e) => setCreating({ ...creating, display_order: Number(e.target.value) })}
              />
            </div>
          </div>
          <button className="btn-green" onClick={() => void create()} disabled={busy}>
            {busy ? '追加中…' : '追加する'}
          </button>
          <p className="hint" style={{ marginTop: 10 }}>
            事業部IDは階層マスタ・取引・予算の紐づけに使うため、後から変更できません。
            追加後はユーザーの所属事業部を設定してください。
          </p>
        </Modal>
      )}

      {editing && (
        <div className="card">
          <h3>事業部を編集: {editing.id}</h3>
          <div className="form-grid">
            <div>
              <label>名称</label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </div>
            <div>
              <label>短縮名</label>
              <input
                value={editing.short_name}
                onChange={(e) => setEditing({ ...editing, short_name: e.target.value })}
              />
            </div>
            <div>
              <label>表示順</label>
              <input
                type="number"
                value={editing.display_order}
                onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })}
              />
            </div>
            <div>
              <label>状態</label>
              <select
                value={editing.active ? '1' : '0'}
                onChange={(e) => setEditing({ ...editing, active: e.target.value === '1' })}
              >
                <option value="1">有効</option>
                <option value="0">無効</option>
              </select>
            </div>
          </div>
          <button className="btn-green" onClick={() => void save()} disabled={busy}>
            保存
          </button>
          <button
            className="btn-line"
            style={{ marginLeft: 10 }}
            onClick={() => setEditing(null)}
            disabled={busy}
          >
            キャンセル
          </button>
        </div>
      )}
    </>
  );
}
